(() => {
  if (customElements.get('video-modal')) {
    return;
  }

  /**
   * Video modal component.
   */
  class VideoModal extends HTMLElement {
    /**
     * Constructor.
     */
    constructor() {
      super();
      this.selectors = {
        button: '[data-button]',
        dialog: '[data-dialog]',
        dialogContent: '[data-dialog-content]',
        closeButton: '[data-close-button]',
        video: '[data-video]'
      }
    }

    /**
     * Cache HTML elements.
     */
    cacheHTMLElements() {
      this.button = this.querySelector(this.selectors.button);
      this.dialog = this.querySelector(this.selectors.dialog);
      this.dialogContent = this.querySelector(this.selectors.dialogContent);
      this.closeButton = this.querySelector(this.selectors.closeButton);
    }

    /**
     * Toggle dialog.
     * 
     * @param {Event} event
     */
    toggleDialog(event) {
      event?.preventDefault();
      if (event && event.type === 'close') {
        this.closeDialog();
        return;
      }

      const isOpen = this.dialog.hasAttribute('open');
      if (isOpen) {
        this.closeDialog();
        return;
      }
      
      this.loadVideo()
        .then(() => {
          this.dialog.classList.add('is-open');
          this.playVideo();
          this.dialog.showModal();
          ScrollLocker.lock()
        });
    }

    /**
     * Play video.
     * 
     * @returns {Void}
     */
    playVideo() {
      if (this.hasAttribute('loaded')) {
        toggleVimeoVideo(this.video);
        return;
      }
      
      const handleLoad = () => {
        toggleVimeoVideo(this.video);
        this.setAttribute('loaded', true);
        this.video.removeEventListener('load', handleLoad);
      }

      this.video.addEventListener('load', handleLoad);
    }

    /**
     * Load video.
     * 
     * @returns {Promise}
     */
    loadVideo() {
      return new Promise((resolve) => {
        if (this.hasAttribute('loaded')) {
          resolve();
          return;
        }
        const content = document.createElement('div');
        content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
        this.dialogContent.appendChild(content.querySelector(this.selectors.video))
        this.video = this.querySelector(this.selectors.video);
        resolve();
      })
    }

    /**
     * Close dialog.
     */
    closeDialog() {
      toggleVimeoVideo(this.video, 'pause');
      this.dialog.classList.remove('is-open');
      setTimeout(() => {
        this.dialog.hasAttribute('open') && this.dialog.close();
        ScrollLocker.unlock();
      }, 500)
    }

    /**
     * Handle dialog click.
     * 
     * @param {Event} event 
     * @returns {Void}
     */
    handleDialogClick(event) {
      const clickInDialog = 
        this.dialogContent.contains(event.target) || event.target === this.dialogContent;
      if (clickInDialog) {
        return;
      }
      this.toggleDialog();
    }

    /**
     * Connected callback.
     */
    connectedCallback() {
      this.cacheHTMLElements();
      if (!this.button || !this.dialog || !this.closeButton) {
        console.error('Missing required elements.');
        return;
      }
      this.toggleListeners();
    }

    disconnectedCallback() {
      this.toggleListeners(true);
    }

    /**
     * Toggle listeners.
     * 
     * @param {Boolean} remove 
     */
    toggleListeners(remove = false) {
      const method = remove ? 'removeEventListener' : 'addEventListener';

      [this.button, this.closeButton].forEach((button) => {
        button[method]('click', this.toggleDialog.bind(this));
      });

      this.dialog[method]('click', this.handleDialogClick.bind(this));
      this.dialog[method]('close', this.toggleDialog.bind(this));
    }
  }

  customElements.define('video-modal', VideoModal);
})();